import { faArrowDown, faArrowRight, faArrowUp, faEdit, faPlus, faTrash } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from '@ui';
import { StyledInput } from '@ui/Form';
import { cx } from 'class-variance-authority';
import { get } from 'lodash';
import { GetServerSideProps } from 'next';
import dynamic from 'next/dynamic';
import Image from 'next/image';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useState } from 'react';
import useSWR from 'swr';
import StepList from '../components/Academy/StepList';
import { Topic, TopicCategory } from '../components/Academy/types';
import Layout from '../components/Layout';
import { fetch, handleFetch } from '../helpers/api';
import { useApi } from '../hooks/useApi';
import useAuth from '../hooks/useAuth';
import { useConfirmedCallback } from '../hooks/useConfirmedCallback';
import { useDebouncedValue } from '../hooks/useDebouncedValue';
import useMediaQuery from '../hooks/useMediaQuery';
import { useModal } from '../hooks/useModal';

const TopicForm = dynamic(() => import('../components/Academy/TopicForm'));
const TopicCategoryForm = dynamic(() => import('../components/Academy/TopicCategoryForm'));

export const getServerSideProps: GetServerSideProps = async (context) => {
    const res = await fetch(`academy`, context);
    return handleFetch(res, (data) => ({
        props: {
            categories: data.data
        }
    }));
};

interface Props {
    categories: TopicCategory[];
}

const AcademyPage = ({ categories }: Props) => {
    const api = useApi();
    const { user } = useAuth();
    const { query: params, push } = useRouter();
    const isOwner = ['mhesse@sportision.de', 'lcakirlar@sportision.de', 'jmacha@sportision.de'].includes(user?.email);
    const [query, setQuery] = useState<string>();
    const debouncedQuery = useDebouncedValue(query, 500);
    const activeTopic = params.topic ? parseInt(params.topic as string) : null;
    const isMobile = useMediaQuery('(max-width: 650px)');
    const [setModal, resetModal] = useModal();
    const { data, mutate } = useSWR(`academy?q=${typeof debouncedQuery === 'string' ? debouncedQuery : ''}`, null, {
        fallbackData: { data: categories },
        revalidateOnFocus: false
    });

    useEffect(() => {
        handleMobileTopic(activeTopic);
    }, [activeTopic]);

    const handleShowTopicForm = useCallback((topic?: Topic) => {
        setModal({
            title: 'Thema',
            content: (
                <TopicForm
                    topic={topic}
                    callback={() => {
                        resetModal();
                        mutate();
                    }}
                />
            ),
            show: true,
            size: 60
        });
    }, []);

    const handleShowTopicCategoryForm = useCallback((category?: TopicCategory) => {
        setModal({
            title: 'Thema Einteilung',
            content: (
                <TopicCategoryForm
                    category={category}
                    callback={() => {
                        resetModal();
                        mutate();
                    }}
                />
            ),
            show: true,
            size: 60
        });
    }, []);

    const handleDeleteTopic = useConfirmedCallback((topic: Topic) => {
        api({
            url: `academy/topic/${topic.id}`,
            method: 'DELETE',
            onSuccess: () => mutate()
        });
    });

    const handleMoveTopic = useCallback((source: number, target: number) => {
        if (!target || !source) return;
        void api({
            url: `academy/topic/${source}/${target}`,
            method: 'GET',
            onSuccess: () => mutate()
        });
    }, []);

    const handleMobileTopic = useCallback(
        (topicId: any) => {
            if (isMobile && topicId)
                setModal({
                    title: 'Anleitung',
                    content: <StepList isOwner={isOwner} topicId={topicId} />,
                    show: true,
                    size: 100,
                    onClose: () => push({}, undefined, { shallow: true })
                });
        },
        [isMobile, isOwner]
    );

    const handleSetActiveTopic = useCallback(
        (topicId: number | null) => {
            if (topicId === null) push({}, undefined, { shallow: true });
            else push({ query: { topic: topicId } }, undefined, { shallow: true });
        },
        [handleMobileTopic]
    );

    return (
        <Layout title="Sportision - Academy">
            <div className="container tw-relative tw-mt-12 tw-overflow-hidden">
                <HeaderBackground />
                <div>
                    <div className="tw-mb-4 tw-mt-12 tw-grid tw-grid-cols-4 tw-gap-8">
                        <div className="tw-col-span-3">
                            <h1>Herzlich Willkommen!</h1>
                            <h3 className="tw-text-accents-7">in der neuen Sportision Academy</h3>
                        </div>
                        <Image
                            alt=""
                            className="!tw-hidden md:!tw-block"
                            src="/images/academy_logo.png"
                            width={6000}
                            height={1000}
                            layout="responsive"
                            objectFit="contain"
                        />
                    </div>
                    <p className="tw-mb-12">
                        Die Sportision Academy ist deine Anlaufstelle für Fragen rund um die Benutzung von Sportision!
                        Du hast eine Frage? Oder möchtest mehr zu deinem Theme erfahren? Dann gebe in die Suche
                        Schlagwörter zu deinem Thema ein und finde noch heute deine Lösung!
                    </p>
                </div>
                <StyledInput
                    fullWidth
                    className="tw-mb-4 tw-hidden md:tw-block"
                    onChange={(e) => {
                        handleSetActiveTopic(null);
                        setQuery(e.target.value);
                    }}
                    placeholder="Suche"
                    value={query}
                />
                <div className="tw-grid tw-grid-cols-1 tw-gap-4 md:tw-grid-cols-3">
                    <div>
                        {isOwner && (
                            <div className="tw-flex tw-items-center tw-gap-4">
                                <Button
                                    auto
                                    icon={<FontAwesomeIcon icon={faPlus} />}
                                    onPress={() => handleShowTopicForm()}
                                    className="tw-mb-4"
                                >
                                    Thema
                                </Button>
                                <Button
                                    auto
                                    icon={<FontAwesomeIcon icon={faPlus} />}
                                    onPress={() => handleShowTopicCategoryForm()}
                                    className="tw-mb-4"
                                >
                                    Einteilung
                                </Button>
                            </div>
                        )}
                        <div>
                            {data?.data?.length > 0 ? (
                                data?.data?.map((category) => (
                                    <div className="tw-card-flat tw-mb-4 tw-pl-0" key={category.id}>
                                        <h5 className="tw-mb-4 tw-ml-4 tw-font-bold">{category.title}</h5>
                                        {category.topics.map((topic, index) => (
                                            <div key={topic.id}>
                                                <div
                                                    className={cx(
                                                        topic.id === activeTopic && '!tw-border-success',
                                                        'tw-my-2 tw-flex tw-cursor-pointer !tw-flex-row tw-items-center tw-justify-between tw-border-0 tw-border-l-4 tw-border-solid tw-border-transparent tw-py-2 tw-pl-4'
                                                    )}
                                                    onClick={() => handleSetActiveTopic(topic.id)}
                                                >
                                                    <span>{topic.title}</span>
                                                    <div className="tw-flex tw-items-center tw-gap-2">
                                                        <FontAwesomeIcon
                                                            icon={faArrowRight}
                                                            className="tw-text-success"
                                                        />
                                                        {isOwner && (
                                                            <div className="tw-flex tw-items-center tw-gap-1">
                                                                <Button
                                                                    onPress={() =>
                                                                        handleMoveTopic(
                                                                            topic.id,
                                                                            get(category.topics, index - 1, null)?.id
                                                                        )
                                                                    }
                                                                    size="xs"
                                                                    color="light"
                                                                >
                                                                    <FontAwesomeIcon icon={faArrowUp} />
                                                                </Button>
                                                                <Button
                                                                    onPress={() =>
                                                                        handleMoveTopic(
                                                                            topic.id,
                                                                            get(category.topics, index + 1, null)?.id
                                                                        )
                                                                    }
                                                                    size="xs"
                                                                    color="light"
                                                                >
                                                                    <FontAwesomeIcon icon={faArrowDown} />
                                                                </Button>
                                                                <Button
                                                                    onPress={() => handleShowTopicForm(topic)}
                                                                    size="xs"
                                                                    color="warning"
                                                                >
                                                                    <FontAwesomeIcon icon={faEdit} />
                                                                </Button>
                                                                <Button
                                                                    onPress={() => handleDeleteTopic(topic)}
                                                                    size="xs"
                                                                    color="error"
                                                                >
                                                                    <FontAwesomeIcon icon={faTrash} />
                                                                </Button>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ))
                            ) : (
                                <span className="tw-text-center tw-text-accents-6">
                                    Keine Ergebnisse unter {`"${debouncedQuery}"`}
                                </span>
                            )}
                        </div>
                    </div>
                    <div className="tw-col-span-2 tw-hidden md:tw-block">
                        <StepList isOwner={isOwner} topicId={activeTopic} />
                    </div>
                </div>
            </div>
        </Layout>
    );
};

const HeaderBackground = () => {
    return (
        <div className="tw-absolute tw-left-0 tw-top-[25rem] -tw-z-10 tw-ml-[-38rem] tw-h-[25rem] tw-w-[calc(100%+48rem)]">
            <div className="tw-absolute tw-inset-0 tw-bg-gradient-to-r tw-opacity-10">
                <svg
                    aria-hidden="true"
                    className="tw-absolute tw-inset-x-0 tw-inset-y-[-50%] tw-h-[200%] tw-w-full tw-skew-y-[-18deg] tw-fill-black/40 tw-stroke-black/50 tw-mix-blend-overlay"
                >
                    <defs>
                        <pattern id=":S1:" width="72" height="56" patternUnits="userSpaceOnUse" x="-12" y="4">
                            <path d="M.5 56V.5H72" fill="none"></path>
                        </pattern>
                    </defs>
                    <rect width="100%" height="100%" fill="url(#:S1:)"></rect>
                    <svg x="-12" y="4" className="overflow-visible tw-fill-successLight">
                        <rect width="73" height="57" x="288" y="168"></rect>
                        <rect width="73" height="57" x="144" y="56"></rect>
                        <rect width="73" height="57" x="504" y="168"></rect>
                        <rect width="73" height="57" x="720" y="336"></rect>
                    </svg>
                </svg>
            </div>
            <svg
                viewBox="0 0 1113 440"
                aria-hidden="true"
                className="tw-absolute tw-left-1/2 tw-top-0 tw-ml-[-19rem] tw-w-[69.5625rem] tw-fill-white tw-blur-[26px]"
            >
                <path d="M.016 439.5s-9.5-300 434-300S882.516 20 882.516 20V0h230.004v439.5H.016Z"></path>
            </svg>
        </div>
    );
};

export default AcademyPage;
